import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Button,
  Skeleton,
  Divider,
  Accordion,
  Modal,
  Textarea,
  Alert,
  Badge,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconRefresh,
  IconX,
  IconTruck,
  IconEdit,
  IconClock,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import {
  useOrder,
  useReorder,
  useCancelOrder,
  useOrderModifications,
  useOrderModifyCutoff,
  useSubmitOrderModification,
  useCancelModification,
} from '@/hooks/usePortalApi';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orderId = Number(id) || 0;
  const { data: order, isLoading } = useOrder(orderId);
  const reorder = useReorder();
  const cancelOrder = useCancelOrder();
  const { data: cutoff } = useOrderModifyCutoff(orderId);
  const { data: modifications } = useOrderModifications(orderId);
  const submitMod = useSubmitOrderModification();
  const cancelMod = useCancelModification();

  const [modifyOpen, setModifyOpen] = useState(false);
  const [modNotes, setModNotes] = useState('');

  const handleReorder = () => {
    reorder.mutate(orderId, {
      onSuccess: (res) => {
        notifications.show({ title: 'Reorder created', message: 'New order has been placed', color: 'brand' });
        navigate(`/orders/${res.data.id}`);
      },
    });
  };

  const handleCancel = () => {
    cancelOrder.mutate(
      { orderId, reason: 'Cancelled by customer' },
      {
        onSuccess: () => {
          notifications.show({ title: 'Order cancelled', message: 'Your order has been cancelled', color: 'orange' });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={30} width={200} />
        <Skeleton height={200} />
      </Stack>
    );
  }

  if (!order) {
    return (
      <Stack gap="lg">
        <Text>Order not found</Text>
        <Button component={Link} to="/orders" variant="subtle">
          Back to orders
        </Button>
      </Stack>
    );
  }

  const canCancel = ['draft', 'confirmed'].includes(order.status);

  return (
    <Stack gap="lg">
      <Group>
        <Button
          component={Link}
          to="/orders"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          size="sm"
          color="gray"
        >
          Back to Orders
        </Button>
      </Group>

      <PageHeader
        title={order.order_number}
        description={`Placed ${dayjs(order.created_at).format('MMM D, YYYY [at] h:mm A')}`}
        actions={
          <>
            <StatusBadge status={order.status} />
            {cutoff?.can_modify && (
              <Button
                variant="light"
                color="yellow"
                leftSection={<IconEdit size={16} />}
                size="sm"
                onClick={() => setModifyOpen(true)}
              >
                Modify Order
                {cutoff.cutoff_at && (
                  <Badge size="xs" variant="light" color="orange" ml={6}>
                    <IconClock size={10} /> {dayjs(cutoff.cutoff_at).fromNow(true)} left
                  </Badge>
                )}
              </Button>
            )}
            <Button
              component={Link}
              to={`/orders/${order.id}/track`}
              variant="light"
              leftSection={<IconTruck size={16} />}
              size="sm"
            >
              Track
            </Button>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              size="sm"
              onClick={handleReorder}
              loading={reorder.isPending}
            >
              Reorder
            </Button>
            {canCancel && (
              <Button
                variant="light"
                color="red"
                leftSection={<IconX size={16} />}
                size="sm"
                onClick={handleCancel}
                loading={cancelOrder.isPending}
              >
                Cancel
              </Button>
            )}
          </>
        }
      />

      {/* Order Info */}
      <Paper p="md">
        <Group gap="xl">
          <div>
            <Text size="xs" c="dimmed" tt="uppercase">Delivery Date</Text>
            <Text size="sm" fw={500}>
              {order.delivery_date ? dayjs(order.delivery_date).format('MMM D, YYYY') : 'TBD'}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase">Total</Text>
            <Text size="sm" fw={700} c="brand">
              ${parseFloat(order.total).toFixed(2)}
            </Text>
          </div>
          {order.notes && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase">Notes</Text>
              <Text size="sm">{order.notes}</Text>
            </div>
          )}
        </Group>
      </Paper>

      {/* Order Lines */}
      {order.lines && order.lines.length > 0 && (
        <Paper>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th w={100} ta="right">Qty</Table.Th>
                <Table.Th w={120} ta="right">Unit Price</Table.Th>
                <Table.Th w={120} ta="right">Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {order.lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>
                    <Text size="sm">Product #{line.product_id}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{line.quantity}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">${parseFloat(line.unit_price).toFixed(2)}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>${parseFloat(line.total).toFixed(2)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Divider />
          <Group justify="flex-end" p="md">
            <Text fw={700} size="lg">
              Total: ${parseFloat(order.total).toFixed(2)}
            </Text>
          </Group>
        </Paper>
      )}
      {/* Modification Cutoff Alert */}
      {cutoff && !cutoff.can_modify && (
        <Alert variant="light" color="orange" title="Modifications Not Available" icon={<IconClock size={16} />}>
          {cutoff.reason || 'The modification window for this order has closed. Please contact support if you need changes.'}
        </Alert>
      )}

      {/* Modify Order Modal */}
      <Modal opened={modifyOpen} onClose={() => setModifyOpen(false)} title="Modify Order">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Describe the modifications you would like to make to this order. Our team will review
            your request and update the order accordingly.
          </Text>
          {cutoff?.cutoff_at && (
            <Alert variant="light" color="orange" icon={<IconClock size={16} />}>
              Modification requests must be submitted before{' '}
              {dayjs(cutoff.cutoff_at).format('MMM D, YYYY [at] h:mm A')}{' '}
              ({dayjs(cutoff.cutoff_at).fromNow()})
            </Alert>
          )}
          <Textarea
            label="Modification Notes"
            placeholder="Describe what changes you need (e.g., change quantities, remove items, update delivery date...)"
            minRows={4}
            value={modNotes}
            onChange={(e) => setModNotes(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setModifyOpen(false)}>
              Cancel
            </Button>
            <Button
              leftSection={<IconEdit size={16} />}
              loading={submitMod.isPending}
              disabled={!modNotes.trim()}
              onClick={() => {
                submitMod.mutate(
                  { orderId, modifications: [], notes: modNotes },
                  {
                    onSuccess: () => {
                      notifications.show({
                        title: 'Modification requested',
                        message: 'Your modification request has been submitted for review',
                        color: 'brand',
                      });
                      setModifyOpen(false);
                      setModNotes('');
                    },
                  },
                );
              }}
            >
              Submit Request
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modification History */}
      {modifications && modifications.length > 0 && (
        <Paper p="md">
          <Accordion>
            <Accordion.Item value="modification-history">
              <Accordion.Control>
                <Group gap="sm">
                  <IconEdit size={18} />
                  <Text fw={600}>Modification History</Text>
                  <Badge size="sm" variant="light">{modifications.length}</Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  {modifications.map((mod: any) => (
                    <Paper key={mod.id || mod.request_number} p="sm" withBorder>
                      <Group justify="space-between" mb="xs">
                        <Group gap="sm">
                          <Text size="sm" fw={500}>{mod.request_number}</Text>
                          <StatusBadge status={mod.status} />
                        </Group>
                        <Text size="xs" c="dimmed">
                          {dayjs(mod.requested_at).format('MMM D, YYYY [at] h:mm A')}
                        </Text>
                      </Group>
                      {mod.notes && (
                        <Text size="sm" c="dimmed" mb="xs">{mod.notes}</Text>
                      )}
                      {mod.status === 'pending' && (
                        <Group justify="flex-end">
                          <Button
                            variant="subtle"
                            color="red"
                            size="xs"
                            leftSection={<IconX size={14} />}
                            loading={cancelMod.isPending}
                            onClick={() => {
                              cancelMod.mutate(
                                { orderId, requestId: mod.id },
                                {
                                  onSuccess: () => {
                                    notifications.show({
                                      title: 'Modification cancelled',
                                      message: 'Your modification request has been cancelled',
                                      color: 'orange',
                                    });
                                  },
                                },
                              );
                            }}
                          >
                            Cancel Request
                          </Button>
                        </Group>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Paper>
      )}
    </Stack>
  );
}
